// vorbelegung.ts — was das „+“ im App-Header aus der aktuellen Seite weiß.
//
// T-230 (Dirk, 07.10.2026): „Dokument innerhalb des Vorgangs anlegen, dann
// sollte alles vorbelegt sein“. Bis dahin fragte die Vorfrage im Vorgang
// Kontakt und Vorgang von vorn ab. Jetzt gilt:
//  - im Vorgang (`/vorgaenge/:id`)          → sein Kunde + der Vorgang selbst
//  - in der Kontaktakte (`/contacts/:id`,
//    `/kontakte/edit/:id`)                   → der Kontakt
// Der Kontakt gilt für alle Vorfragen (Risiko, Vertrag, Schaden, Dokument),
// der Vorgang nur beim Dokument. Ein Vorgang ohne Kunden (persönlich) belegt
// nichts vor — ein Dokument braucht immer einen Kontakt, und der Vorgang wird
// erst unter dem Kontakt gewählt.

import { displayContactName } from "../../utils/contactDisplay";
import { contactTypeLabel } from "../../utils/contactTypeLabel";
import type { KontaktTreffer } from "./KontaktSuche";
import type { NeuAnlageVorbelegung } from "./NeuAnlageDialog";

export type SeitenKontext = { art: "vorgang" | "kontakt"; id: number };

const MUSTER: ReadonlyArray<[SeitenKontext["art"], RegExp]> = [
  // `/vorgaenge/v4/:id` ist der zweite Weg zur selben Vorgangsseite (App.tsx).
  ["vorgang", /^\/vorgaenge\/(?:v4\/)?(\d+)(?:\/|$)/],
  // Unterseiten der Akte (`/contacts/:id/auswertung` …) gehören zum selben Kontakt.
  ["kontakt", /^\/contacts\/(\d+)(?:\/|$)/],
  ["kontakt", /^\/kontakte\/edit\/(\d+)(?:\/|$)/],
];

/** Auf welcher Vorgangs- oder Kontaktseite steht der Nutzer? Sonst `null`. */
export function seitenKontext(pfad: string): SeitenKontext | null {
  for (const [art, re] of MUSTER) {
    const m = re.exec(pfad);
    if (m) return { art, id: Number(m[1]) };
  }
  return null;
}

/** Roh-Kontakt aus der API (Liste, Akte oder Kunden-Summary eines Vorgangs). */
export type RohKontakt = NonNullable<Parameters<typeof displayContactName>[0]> & { id?: number | string };

/** Roh-Kontakt → Treffer, wie ihn die Kontaktsuche der Vorfrage zeigt. */
export const kontaktTrefferAus = (c: RohKontakt): KontaktTreffer => {
  const typ = typeof c.type === "string" ? c.type : c.type?.type;
  return {
    id: Number(c.id),
    name: displayContactName(c) ?? `Kontakt #${c.id}`,
    typ: typ ? String(contactTypeLabel(typ)) : null,
  };
};

type RohVorgang = {
  id: number;
  titel?: string | null;
  vorgangNbr?: string | null;
  status?: string | null;
  kunde?: RohKontakt | null;
};

/**
 * Lädt, was zur Seite gehört. Jeder Fehler (gelöscht, keine Rechte, Netz)
 * endet in `null` — dann fragt der Dialog wie bisher alles ab.
 */
export async function vorbelegungErmitteln(
  kontext: SeitenKontext,
  laden: {
    vorgangLaden: (id: number) => Promise<RohVorgang>;
    kontaktLaden: (id: number) => Promise<RohKontakt>;
  },
): Promise<NeuAnlageVorbelegung | null> {
  try {
    if (kontext.art === "kontakt") {
      const k = await laden.kontaktLaden(kontext.id);
      return Number(k?.id) > 0 ? { kontakt: kontaktTrefferAus(k), vorgang: null } : null;
    }
    const v = await laden.vorgangLaden(kontext.id);
    if (!v?.kunde || !(Number(v.kunde.id) > 0)) return null;
    return {
      kontakt: kontaktTrefferAus(v.kunde),
      vorgang: {
        id: v.id,
        titel: (v.titel ?? "").trim(),
        nummer: v.vorgangNbr ?? null,
        // „Erledigt" ist der einzige geschlossene Status (wie vorgaengeFuerAuswahl).
        offen: v.status !== "Erledigt",
      },
    };
  } catch {
    return null;
  }
}
